// @atlas/contracts — a `clients` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { ClientBillingProfileSchema, ClientStatusSchema } from '../_shared/schemas.js';
/** Üres string → null (mező-törlés/„nincs érték"): a legacy „üres → null" szabály. */
const clientsEmptyToNull = (v) => typeof v === 'string' && v.trim() === '' ? null : v;
/**
 * Gazdag ügyfél-sor (lista/detail közös alap): a platform-mag + a `clients/client` profil BFF-merge-e. A
 * `status` STRING (nem enum): a legacy adat-parity fallback nyers értéket is hozhat (a validált enum a
 * write-oldalon hat). Az `accountsCount` a legacy „Fiókok" oszlop (a listában BATCH-elve, nem N+1). A
 * `nasFolder` szándékosan NINCS a szerződésben (kivezetés alatt — migrációs döntés #4).
 */
/** Egy fiók-tétel a lista „Fiókok" oszlopához (legacy accounts-parity): id + név, név szerint rendezve. */
export const ClientAccountItemSchema = z.object({
    id: z.string(),
    name: z.string(),
});
export const ClientRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    status: z.string(),
    autoBilling: z.boolean(),
    createdAt: z.string().nullable(),
    /** A hozzá tartozó (nem-törölt) fiók-profilok száma (`accounts`) — a legacy „Fiókok" oszlop számlálója. */
    accountsCount: z.number(),
    /** A fiók-nevek (név szerint rendezve) — a „Fiókok" oszlop név-listája/legördülője (BATCH-elt, nem N+1). */
    accounts: z.array(ClientAccountItemSchema),
    /** A számlázási profilok (elsődleges elöl) — a „Számlázási adatok" oszlop (BATCH-elt, best-effort). */
    billingProfiles: z.array(ClientBillingProfileSchema),
});
/** Ügyfél-detail: a sor + OCC-verzió (a profil `meta.version`-je, az edit If-Match forrása). */
export const ClientDetailSchema = ClientRowSchema.extend({
    version: z.number(),
});
/**
 * Ügyfél-lista lekérdezés: név/email keresés (`q`), státusz-szűrő, rendezés-irány (a rendezés-KULCS fixen
 * `name`, magyar collation — ezért nincs `sort`), végtelen görgetéshez offset-lapozás (default `limit` 100).
 */
export const ClientListQuerySchema = z.object({
    q: z.string().optional(),
    status: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
    cursor: z.string().optional(),
});
/** Ügyfél-lista válasz: a lapnyi sor + a SZŰRT teljes darabszám + a következő offset (null → nincs több). */
export const ClientListResponseSchema = z.object({
    clients: z.array(ClientRowSchema),
    total: z.number(),
    nextOffset: z.number().nullable(),
});
/** Ügyfél létrehozás body: name kötelező (≤255); email opcionális email-formátum; phone ≤20; üres → null. */
export const ClientCreateBodySchema = z.object({
    name: z.string().trim().min(1).max(255),
    email: z.preprocess(clientsEmptyToNull, z.string().email().max(255).nullable().optional()),
    phone: z.preprocess(clientsEmptyToNull, z.string().max(20).nullable().optional()),
    status: ClientStatusSchema.optional(),
    autoBilling: z.boolean().optional(),
});
/** Ügyfél-frissítés body: minden mező opcionális (részleges PATCH); email/phone explicit null → törlés. */
export const ClientUpdateBodySchema = z.object({
    name: z.string().trim().min(1).max(255).optional(),
    email: z.preprocess(clientsEmptyToNull, z.string().email().max(255).nullable().optional()),
    phone: z.preprocess(clientsEmptyToNull, z.string().max(20).nullable().optional()),
    status: ClientStatusSchema.optional(),
    autoBilling: z.boolean().optional(),
});
/** Idempotencia-kulcs a create-en (kettős-POST elleni védelem — az Odin foundation érvényesíti). */
export const ClientIdempotencyHeaders = z.object({ 'idempotency-key': z.string().optional() });
/** Optimista lock az update/delete-en (a kliens által olvasott `version` → Odin `If-Match`). */
export const ClientIfMatchHeaders = z.object({ 'if-match': z.string().optional() });
//# sourceMappingURL=schemas.js.map